import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useAppBindingList,
  useSecureAccessSettings,
} from "./hooks/useAppBindings";
import type {
  OwnershipScanContinuationResponse,
  SecureAccessVerification,
} from "@/api/kintone-environment";
import { useAuth } from "@/hooks/useAuth";
import {
  clientCertificatePasswordValidationError,
  resolveClientCertificateExpiryState,
} from "./secure-access-ux";
import { ApiError } from "@/api/client";

const MAX_CERTIFICATE_BYTES = 1_048_576;

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("read_failed"));
    reader.readAsDataURL(file);
  });
}

function formatDate(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("ja-JP");
}

/** 組織singletonのwrite-onlyクライアント証明書管理。 */
export function SecureAccessSettings() {
  const { user } = useAuth();
  const kintoneSubdomain = user?.serviceSubscription?.kintoneSubdomain ?? "";
  const {
    view,
    isLoading,
    error: loadError,
    refresh,
    replaceCertificate,
    disable,
  } = useSecureAccessSettings();
  const { items: appBindings, isLoading: appBindingsLoading } = useAppBindingList({});
  const [file, setFile] = useState<File | null>(null);
  const [passphrase, setPassphrase] = useState("");
  const [verificationKind, setVerificationKind] =
    useState<"saved_app_credentials" | "new_api_token">("saved_app_credentials");
  const [kintoneAppUrl, setKintoneAppUrl] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [scan, setScan] = useState<OwnershipScanContinuationResponse | null>(null);
  const [pendingAction, setPendingAction] = useState<"replace" | "disable" | null>(null);
  const [guestSpaceId, setGuestSpaceId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [disableConfirmed, setDisableConfirmed] = useState(false);
  const replaceIdempotencyKey = useRef<string | null>(null);
  const disableIdempotencyKey = useRef<string | null>(null);
  const certificateInput = useRef<HTMLInputElement | null>(null);
  const previousCustomerId = useRef(user?.serviceSubscription?.customerId ?? null);

  useEffect(() => {
    if (appBindings.length === 0 && !appBindingsLoading) {
      setVerificationKind("new_api_token");
    } else if (appBindings.length > 0) {
      setVerificationKind("saved_app_credentials");
    }
  }, [appBindings.length, appBindingsLoading]);

  useEffect(() => {
    const customerId = user?.serviceSubscription?.customerId ?? null;
    if (previousCustomerId.current !== customerId) {
      previousCustomerId.current = customerId;
      setFile(null);
      setPassphrase("");
      setApiToken("");
      setKintoneAppUrl("");
      setScan(null);
      setPendingAction(null);
      replaceIdempotencyKey.current = null;
      disableIdempotencyKey.current = null;
      if (certificateInput.current) certificateInput.current.value = "";
    }
  }, [user?.serviceSubscription?.customerId]);

  const expiry = resolveClientCertificateExpiryState(
    view?.connectionKind === "secure_access",
    view?.expiresAt ?? null,
  );

  function invalidateMutationKeys() {
    replaceIdempotencyKey.current = null;
    disableIdempotencyKey.current = null;
    setMessage(null);
    setError(null);
    setErrorCode(null);
    setScan(null);
    setPendingAction(null);
    setGuestSpaceId("");
  }

  function verification(
    targetScopeDecision?:
      | { kind: "regular_space" }
      | { kind: "guest_space"; guestSpaceId: string },
  ): SecureAccessVerification | null {
    if (verificationKind === "saved_app_credentials") {
      if (targetScopeDecision) {
        return scan?.state === "target_scope_input_required"
          ? {
              kind: "saved_app_credentials",
              scanId: scan.scanId,
              targetScopeDecision,
            }
          : null;
      }
      if (scan?.state === "inactive_expired") {
        return {
          kind: "saved_app_credentials",
          scanId: scan.scanId,
          continuationCursor: scan.continuationCursor,
        };
      }
      return {
        kind: "saved_app_credentials",
        ...(scan ? { scanId: scan.scanId } : {}),
        ...(targetScopeDecision ? { targetScopeDecision } : {}),
      };
    }
    const appUrl = kintoneAppUrl.trim();
    const token = apiToken.trim();
    return appUrl && token
      ? { kind: "new_api_token", kintoneAppUrl: appUrl, apiToken: token }
      : null;
  }

  async function handleReplaceCertificate(
    targetScopeDecision?: Parameters<typeof verification>[0],
  ) {
    const verificationInput = verification(targetScopeDecision);
    if (!file) {
      setError("新しいクライアント証明書（.pfx / .p12）を選択してください。");
      return;
    }
    if (file.size > MAX_CERTIFICATE_BYTES) {
      setError("クライアント証明書は1 MiB以下にしてください。");
      return;
    }
    const passwordValidationError = clientCertificatePasswordValidationError(passphrase);
    if (passwordValidationError) {
      setError(passwordValidationError);
      return;
    }
    if (!verificationInput) {
      setError("保存済みアプリ設定、または完全なアプリURLとAPIトークンを指定してください。");
      return;
    }
    setIsSaving(true);
    setMessage(null);
    setError(null);
    setErrorCode(null);
    try {
      const clientCertBase64 = await readFileAsBase64(file);
      replaceIdempotencyKey.current ??= globalThis.crypto.randomUUID();
      const result = await replaceCertificate({
          expectedRevision: view?.revision ?? null,
          connectionAccess: {
            kind: "secure_access",
            clientCertBase64,
            passphrase,
          },
          verification: verificationInput,
        }, replaceIdempotencyKey.current);
      if (!("connectionKind" in result)) {
        setScan(result);
        setPendingAction("replace");
        return;
      }
      replaceIdempotencyKey.current = null;
      setFile(null);
      if (certificateInput.current) certificateInput.current.value = "";
      setPassphrase("");
      setApiToken("");
      setScan(null);
      setPendingAction(null);
      setMessage("新しい証明書で接続を確認し、セキュアアクセス設定を更新しました。");
    } catch (err) {
      setErrorCode(err instanceof ApiError ? err.code : null);
      setError(mutationErrorMessage(err, "証明書を更新できませんでした。"));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDisable(
    targetScopeDecision?: Parameters<typeof verification>[0],
  ) {
    const verificationInput = verification(targetScopeDecision);
    if (!view?.revision || !verificationInput || !disableConfirmed) {
      setError("通常接続の確認に使うアプリとAPIトークンを指定してください。");
      return;
    }
    setIsSaving(true);
    setMessage(null);
    setError(null);
    setErrorCode(null);
    try {
      disableIdempotencyKey.current ??= globalThis.crypto.randomUUID();
      const result = await disable({
          expectedRevision: view.revision,
          verification: verificationInput,
        }, disableIdempotencyKey.current);
      if (!("connectionKind" in result)) {
        setScan(result);
        setPendingAction("disable");
        return;
      }
      disableIdempotencyKey.current = null;
      setApiToken("");
      setDisableConfirmed(false);
      setScan(null);
      setPendingAction(null);
      setMessage("通常接続を確認し、セキュアアクセスを無効化しました。");
    } catch (err) {
      setErrorCode(err instanceof ApiError ? err.code : null);
      setError(mutationErrorMessage(err, "セキュアアクセスを無効化できませんでした。"));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>kintoneへの接続方法</CardTitle>
        <CardDescription>
          この組織の全kintoneアプリとサーバ処理に共通するクライアント証明書を管理します。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          セキュアアクセスでは {kintoneSubdomain || "登録済みサブドメイン"}.s.cybozu.com
          に接続します。本サービスの送信元IPをkintone側のIP制限に許可登録してください。
        </p>

        {isLoading ? (
          <div className="text-sm text-muted-foreground">読み込み中…</div>
        ) : loadError ? (
          <div role="alert" className="text-sm text-destructive">{loadError}</div>
        ) : (
          <>
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              接続方法: {view?.connectionKind === "secure_access" ? "セキュアアクセス" : "通常接続"}
              <span className="ml-3">
                証明書: {view?.configurationState === "configured"
                  ? "設定済み"
                  : view?.configurationState === "unavailable" ? "利用不可" : "未設定"}
              </span>
              {view?.connectionKind === "secure_access" ? (
                <span className="ml-2 text-xs text-muted-foreground">
                  有効期限: {formatDate(view.expiresAt)}
                </span>
              ) : null}
            </div>

            {view?.connectionKind === "secure_access" && ["not_recorded", "expired", "expiring_soon"].includes(expiry) ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <span>
                  {expiry === "expired"
                    ? "クライアント証明書の有効期限が切れています。再アップロードしてください。"
                    : expiry === "expiring_soon"
                      ? "クライアント証明書の有効期限が近づいています。更新を検討してください。"
                      : "クライアント証明書の有効期限が未登録です。期限を確認して再アップロードしてください。"}
                </span>
              </div>
            ) : null}

            <fieldset className="space-y-3 rounded-md border p-3">
              <legend className="px-1 text-sm font-medium">接続確認に使うアプリ</legend>
              {appBindings.length > 0 ? (
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="secure-access-verification"
                    checked={verificationKind === "saved_app_credentials"}
                    onChange={() => { setVerificationKind("saved_app_credentials"); invalidateMutationKeys(); }}
                  />
                  <span className="flex-1">
                    <span className="font-medium">保存済みアプリ設定を使う</span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      保存済みの資格情報を順に確認し、1件でも成功すれば検証完了とします。
                    </span>
                  </span>
                </label>
              ) : null}
              {appBindings.length === 0 ? <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="secure-access-verification"
                  checked={verificationKind === "new_api_token"}
                  onChange={() => { setVerificationKind("new_api_token"); invalidateMutationKeys(); }}
                />
                <span className="font-medium">新しいAPIトークンを今回だけ使う</span>
              </label> : null}
              {verificationKind === "new_api_token" ? (
                <div className="grid gap-3 pl-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="secureAccessVerificationAppUrl">完全なアプリURL</Label>
                    <Input id="secureAccessVerificationAppUrl" type="url" placeholder={`https://${kintoneSubdomain || "subdomain"}.cybozu.com/k/123/`} value={kintoneAppUrl} onChange={(event) => { setKintoneAppUrl(event.target.value); invalidateMutationKeys(); }} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="secureAccessVerificationApiToken">APIトークン</Label>
                    <Input id="secureAccessVerificationApiToken" type="password" autoComplete="off" value={apiToken} onChange={(event) => { setApiToken(event.target.value); invalidateMutationKeys(); }} />
                  </div>
                </div>
              ) : null}
            </fieldset>

            <div className="space-y-2">
              <Label htmlFor="secureAccessCert">新しいクライアント証明書（.pfx / .p12）</Label>
              <input
                ref={certificateInput}
                id="secureAccessCert"
                type="file"
                accept=".pfx,.p12,application/x-pkcs12"
                onChange={(event) => { setFile(event.target.files?.[0] ?? null); invalidateMutationKeys(); }}
                className="block w-full text-sm"
              />
              <p className="text-xs text-muted-foreground">
                接続確認に成功した場合だけ暗号化保存し、画面には再表示しません。
              </p>
            </div>

            {file ? (
              <div className="space-y-2">
                <div className="space-y-2">
                  <Label htmlFor="secureAccessPassphrase">クライアント証明書のパスワード（必須）</Label>
                  <Input id="secureAccessPassphrase" type="password" value={passphrase} onChange={(event) => { setPassphrase(event.target.value); invalidateMutationKeys(); }} autoComplete="off" required />
                </div>
                <p className="text-xs text-muted-foreground">有効期間は証明書から自動で読み取ります。</p>
              </div>
            ) : null}

            {scan?.state === "target_scope_input_required" ? (
              <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
                <p>
                  「{scan.appDisplayName ?? "保存済みアプリ"}」が通常スペースかゲストスペースか確認してください。
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => {
                    if (pendingAction === "replace") void handleReplaceCertificate({ kind: "regular_space" });
                    if (pendingAction === "disable") void handleDisable({ kind: "regular_space" });
                  }} disabled={isSaving}>通常スペース</Button>
                  <Input aria-label="ゲストスペースID" inputMode="numeric" className="w-40" value={guestSpaceId} onChange={(event) => setGuestSpaceId(event.target.value)} placeholder="ゲストスペースID" />
                  <Button type="button" size="sm" variant="outline" onClick={() => {
                    if (!/^[1-9][0-9]*$/.test(guestSpaceId)) {
                      setError("正の整数のゲストスペースIDを入力してください。");
                      return;
                    }
                    const decision = { kind: "guest_space" as const, guestSpaceId };
                    if (pendingAction === "replace") void handleReplaceCertificate(decision);
                    if (pendingAction === "disable") void handleDisable(decision);
                  }} disabled={isSaving}>ゲストスペースとして確認</Button>
                </div>
              </div>
            ) : null}

            {scan?.state === "inactive_expired" ? (
              <Button type="button" size="sm" variant="outline" onClick={() => {
                if (pendingAction === "replace") void handleReplaceCertificate();
                if (pendingAction === "disable") void handleDisable();
              }} disabled={isSaving}>接続確認を再開</Button>
            ) : null}

            {scan?.state === "waiting_retry" ? (
              <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" aria-live="polite">
                <p>一時的に接続確認を完了できませんでした。同じ候補から再開できます。</p>
                <Button type="button" size="sm" variant="outline" onClick={() => {
                  if (pendingAction === "replace") void handleReplaceCertificate();
                  if (pendingAction === "disable") void handleDisable();
                }} disabled={isSaving}>接続確認を再開</Button>
              </div>
            ) : null}

            {message ? <div className="rounded-md bg-primary/10 p-2 text-xs">{message}</div> : null}
            {error ? <div role="alert" className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div> : null}
            {errorCode === "secure_access_setting_revision_conflict" ? (
              <Button type="button" size="sm" variant="outline" onClick={() => { invalidateMutationKeys(); void refresh(); }}>
                最新状態を再読み込み
              </Button>
            ) : null}

            {view?.connectionKind === "secure_access" ? (
              <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950">
                <input type="checkbox" checked={disableConfirmed} onChange={(event) => { setDisableConfirmed(event.target.checked); disableIdempotencyKey.current = null; }} />
                <span>通常接続でcurrent環境を確認できた場合、組織の全アプリとサーバ処理を通常hostへ切り替え、保存済み証明書を消去することを確認しました。</span>
              </label>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => void handleReplaceCertificate()}
                disabled={isSaving || scan !== null}
              >
                {view?.connectionKind === "secure_access" ? "証明書を更新" : "セキュアアクセスを有効化"}
              </Button>
              {view?.connectionKind === "secure_access" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void handleDisable()}
                  disabled={isSaving || scan !== null || !disableConfirmed}
                >
                  通常接続へ切り替える
                </Button>
              ) : null}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function mutationErrorMessage(error: unknown, fallback: string) {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : fallback;
  }
  switch (error.code) {
    case "secure_access_setting_revision_conflict":
      return "別の管理者が証明書設定を更新しました。最新状態を再読み込みして確認してください。";
    case "secure_access_mutation_idempotency_unavailable":
      return "設定変更の成否を確定できません。同じ入力のまま再試行してください。";
    case "secure_access_verification_material_required":
      return "接続確認に使うアプリまたはAPIトークンを確認してください。";
    case "kintone_upstream_timeout":
    case "kintone_upstream_unreachable":
      return "kintoneへの接続確認を完了できませんでした。設定を保存せず停止しました。";
    default:
      return error.message || fallback;
  }
}
